import BaseApi from '@core/api/model';

import RatePlanStatusEnum from '@constants/enums/ratePlan/status';
import RatePlanTypeEnum from '@constants/enums/ratePlan/type';
import SubscriptionTypeEnum from '@constants/enums/subscription/type';

import { getUpcomingSubscription, normaliseDateRange } from '@packages/rate-plans/utils/currentSubscription';
import { normaliseSubscriptionItem } from '@packages/subscriptions/utils/normaliseSubscriptionItem';
import { isSubscriptionCancellable } from '@packages/subscriptions/utils/subscriptionCancel';

import { wait } from '@utils/async';

import clock from '@plugins/dayjs';

import RatePlan from '@/models/RatePlan';

const MOCK_ADVISERS = [
    { id: 'adviser-mock-1', name: 'Alpha Advisers Ltd' },
    { id: 'adviser-mock-2', name: 'Beta Wealth Partners' },
    { id: 'adviser-mock-3', name: 'Gamma Financial Planning' }
];

const MOCK_INVESTORS = [
    { id: 'investor-mock-1', name: 'Alice Smith' },
    { id: 'investor-mock-2', name: 'Bob Jones' },
    { id: 'investor-mock-3', name: 'Carol White' },
    { id: 'investor-mock-4', name: 'David Brown' }
];

export class SubscriptionApi extends BaseApi {
    static resource = 'subscriptions';
    static mockItemCount = 12;
    static mockUseFilterOverrides = true;
    static mockFilterOverrideKeys = ['investor_id', 'adviser_id', 'rate_plan_id', 'billing_group_id'];

    get endpoints() {
        return {
            ...super.endpoints,
            create: this.create.bind(this),
            cancel: this.cancel.bind(this)
        };
    }

    get mock_endpoints() {
        return {
            ...super.mock_endpoints,
            create: this.create_mock.bind(this),
            cancel: this.cancel_mock.bind(this)
        };
    }

    _normaliseSubscriptionItem(item = {}) {
        return normaliseSubscriptionItem(item);
    }

    _normaliseSubscriptionData(data) {
        if (Array.isArray(data)) {
            return data.map(item => this._normaliseSubscriptionItem(item));
        }

        return this._normaliseSubscriptionItem(data);
    }

    _filterResponseData(data) {
        return this._normaliseSubscriptionData(super._filterResponseData(data));
    }

    _buildMockCatalogue() {
        const nextMonthStart = clock().tz('Europe/London').add(1, 'month').startOf('month').format('YYYY-MM-DD');
        const currentMonthEnd = clock(nextMonthStart).tz('Europe/London').subtract(1, 'day').format('YYYY-MM-DD');
        const pastEnd = '2023-12-31';
        const pastStart = '2023-01-01';

        const rows = [
            {
                id: 'subscription-mock-current-adviser-1',
                gi_ref: 'GIBS0001',
                billing_group_id: 'billing-group-mock-1',
                billing_group: {
                    id: 'billing-group-mock-1',
                    name: 'Smith Family Office',
                    gi_ref: 'GIBG0001',
                    investors_count: 2
                },
                rate_plan_id: 'rate-plan-adviser-mock-1',
                rate_plan: {
                    id: 'rate-plan-adviser-mock-1',
                    name: 'Standard adviser fees',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.ADVISER,
                    adviser_id: MOCK_ADVISERS[0].id
                },
                subscription_type: SubscriptionTypeEnum.ADVISER,
                date_range: { start: '2024-01-01', end: null },
                investor_id: MOCK_INVESTORS[0].id,
                adviser_id: MOCK_ADVISERS[0].id,
                adviser: MOCK_ADVISERS[0],
                investors_count: 2
            },
            {
                id: 'subscription-mock-current-platform-1',
                gi_ref: 'GIBS0002',
                billing_group_id: 'billing-group-mock-1',
                billing_group: {
                    id: 'billing-group-mock-1',
                    name: 'Smith Family Office',
                    gi_ref: 'GIBG0001',
                    investors_count: 2
                },
                rate_plan_id: 'rate-plan-platform-mock-1',
                rate_plan: {
                    id: 'rate-plan-platform-mock-1',
                    name: 'Platform 0.25% AUA',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.PLATFORM,
                    adviser_id: MOCK_ADVISERS[0].id
                },
                subscription_type: SubscriptionTypeEnum.PLATFORM,
                date_range: { start: '2024-01-01', end: null },
                investor_id: MOCK_INVESTORS[0].id,
                adviser_id: MOCK_ADVISERS[0].id,
                adviser: MOCK_ADVISERS[0],
                investors_count: 2
            },
            {
                id: 'subscription-mock-scheduled-adviser-1',
                gi_ref: 'GIBS0003',
                billing_group_id: 'billing-group-mock-1',
                billing_group: {
                    id: 'billing-group-mock-1',
                    name: 'Smith Family Office',
                    gi_ref: 'GIBG0001',
                    investors_count: 2
                },
                rate_plan_id: 'rate-plan-adviser-mock-2',
                rate_plan: {
                    id: 'rate-plan-adviser-mock-2',
                    name: 'Premium adviser fees',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.ADVISER,
                    adviser_id: MOCK_ADVISERS[0].id
                },
                subscription_type: SubscriptionTypeEnum.ADVISER,
                date_range: { start: nextMonthStart, end: null },
                investor_id: MOCK_INVESTORS[0].id,
                adviser_id: MOCK_ADVISERS[0].id,
                adviser: MOCK_ADVISERS[0],
                investors_count: 2
            },
            {
                id: 'subscription-mock-ended-adviser-1',
                gi_ref: 'GIBS0004',
                billing_group_id: 'billing-group-mock-1',
                billing_group: {
                    id: 'billing-group-mock-1',
                    name: 'Smith Family Office',
                    gi_ref: 'GIBG0001',
                    investors_count: 2
                },
                rate_plan_id: 'rate-plan-adviser-mock-legacy',
                rate_plan: {
                    id: 'rate-plan-adviser-mock-legacy',
                    name: 'Legacy adviser fees',
                    status: RatePlanStatusEnum.DEPRECATED,
                    type: RatePlanTypeEnum.ADVISER,
                    adviser_id: MOCK_ADVISERS[0].id
                },
                subscription_type: SubscriptionTypeEnum.ADVISER,
                date_range: { start: pastStart, end: pastEnd },
                investor_id: MOCK_INVESTORS[0].id,
                adviser_id: MOCK_ADVISERS[0].id,
                adviser: MOCK_ADVISERS[0],
                investors_count: 2
            },
            {
                id: 'subscription-mock-current-adviser-1-ending',
                gi_ref: 'GIBS0005',
                billing_group_id: 'billing-group-mock-1',
                billing_group: {
                    id: 'billing-group-mock-1',
                    name: 'Smith Family Office',
                    gi_ref: 'GIBG0001',
                    investors_count: 2
                },
                rate_plan_id: 'rate-plan-adviser-mock-1',
                rate_plan: {
                    id: 'rate-plan-adviser-mock-1',
                    name: 'Standard adviser fees',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.ADVISER,
                    adviser_id: MOCK_ADVISERS[0].id
                },
                subscription_type: SubscriptionTypeEnum.ADVISER,
                date_range: { start: '2024-01-01', end: currentMonthEnd },
                investor_id: MOCK_INVESTORS[0].id,
                adviser_id: MOCK_ADVISERS[0].id,
                adviser: MOCK_ADVISERS[0],
                investors_count: 2
            },
            {
                id: 'subscription-mock-solo-adviser-2',
                gi_ref: 'GIBS0006',
                billing_group_id: 'billing-group-mock-2',
                billing_group: {
                    id: 'billing-group-mock-2',
                    name: 'Bob Jones',
                    gi_ref: 'GIBG0002',
                    investors_count: 1
                },
                rate_plan_id: 'rate-plan-adviser-mock-3',
                rate_plan: {
                    id: 'rate-plan-adviser-mock-3',
                    name: 'Beta standard fees',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.ADVISER,
                    adviser_id: MOCK_ADVISERS[1].id
                },
                subscription_type: SubscriptionTypeEnum.ADVISER,
                date_range: { start: '2024-06-01', end: null },
                investor_id: MOCK_INVESTORS[1].id,
                adviser_id: MOCK_ADVISERS[1].id,
                adviser: MOCK_ADVISERS[1],
                investors_count: 1
            },
            {
                id: 'subscription-mock-solo-platform-2',
                gi_ref: 'GIBS0007',
                billing_group_id: 'billing-group-mock-2',
                billing_group: {
                    id: 'billing-group-mock-2',
                    name: 'Bob Jones',
                    gi_ref: 'GIBG0002',
                    investors_count: 1
                },
                rate_plan_id: 'rate-plan-platform-mock-2',
                rate_plan: {
                    id: 'rate-plan-platform-mock-2',
                    name: 'Beta platform plan',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.PLATFORM,
                    adviser_id: MOCK_ADVISERS[1].id
                },
                subscription_type: SubscriptionTypeEnum.PLATFORM,
                date_range: { start: '2024-06-01', end: null },
                investor_id: MOCK_INVESTORS[1].id,
                adviser_id: MOCK_ADVISERS[1].id,
                adviser: MOCK_ADVISERS[1],
                investors_count: 1
            },
            {
                id: 'subscription-mock-scheduled-platform-2',
                gi_ref: 'GIBS0008',
                billing_group_id: 'billing-group-mock-2',
                billing_group: {
                    id: 'billing-group-mock-2',
                    name: 'Bob Jones',
                    gi_ref: 'GIBG0002',
                    investors_count: 1
                },
                rate_plan_id: 'rate-plan-platform-mock-3',
                rate_plan: {
                    id: 'rate-plan-platform-mock-3',
                    name: 'Beta premium platform',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.PLATFORM,
                    adviser_id: MOCK_ADVISERS[1].id
                },
                subscription_type: SubscriptionTypeEnum.PLATFORM,
                date_range: { start: nextMonthStart, end: null },
                investor_id: MOCK_INVESTORS[1].id,
                adviser_id: MOCK_ADVISERS[1].id,
                adviser: MOCK_ADVISERS[1],
                investors_count: 1
            },
            {
                id: 'subscription-mock-current-platform-2-ending',
                gi_ref: 'GIBS0009',
                billing_group_id: 'billing-group-mock-2',
                billing_group: {
                    id: 'billing-group-mock-2',
                    name: 'Bob Jones',
                    gi_ref: 'GIBG0002',
                    investors_count: 1
                },
                rate_plan_id: 'rate-plan-platform-mock-2',
                rate_plan: {
                    id: 'rate-plan-platform-mock-2',
                    name: 'Beta platform plan',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.PLATFORM,
                    adviser_id: MOCK_ADVISERS[1].id
                },
                subscription_type: SubscriptionTypeEnum.PLATFORM,
                date_range: { start: '2024-06-01', end: currentMonthEnd },
                investor_id: MOCK_INVESTORS[1].id,
                adviser_id: MOCK_ADVISERS[1].id,
                adviser: MOCK_ADVISERS[1],
                investors_count: 1
            },
            {
                id: 'subscription-mock-gamma-adviser',
                gi_ref: 'GIBS0010',
                billing_group_id: 'billing-group-mock-3',
                billing_group: {
                    id: 'billing-group-mock-3',
                    name: 'Carol White',
                    gi_ref: 'GIBG0003',
                    investors_count: 1
                },
                rate_plan_id: 'rate-plan-adviser-mock-4',
                rate_plan: {
                    id: 'rate-plan-adviser-mock-4',
                    name: 'Gamma adviser plan',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.ADVISER,
                    adviser_id: MOCK_ADVISERS[2].id
                },
                subscription_type: SubscriptionTypeEnum.ADVISER,
                date_range: { start: '2025-01-01', end: null },
                investor_id: MOCK_INVESTORS[2].id,
                adviser_id: MOCK_ADVISERS[2].id,
                adviser: MOCK_ADVISERS[2],
                investors_count: 1
            },
            {
                id: 'subscription-mock-gamma-platform',
                gi_ref: 'GIBS0011',
                billing_group_id: 'billing-group-mock-3',
                billing_group: {
                    id: 'billing-group-mock-3',
                    name: 'Carol White',
                    gi_ref: 'GIBG0003',
                    investors_count: 1
                },
                rate_plan_id: 'rate-plan-platform-mock-4',
                rate_plan: {
                    id: 'rate-plan-platform-mock-4',
                    name: 'Gamma platform plan',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.PLATFORM,
                    adviser_id: MOCK_ADVISERS[2].id
                },
                subscription_type: SubscriptionTypeEnum.PLATFORM,
                date_range: { start: '2025-01-01', end: null },
                investor_id: MOCK_INVESTORS[2].id,
                adviser_id: MOCK_ADVISERS[2].id,
                adviser: MOCK_ADVISERS[2],
                investors_count: 1
            },
            {
                id: 'subscription-mock-shared-group-adviser',
                gi_ref: 'GIBS0012',
                billing_group_id: 'billing-group-mock-4',
                billing_group: {
                    id: 'billing-group-mock-4',
                    name: 'Brown Family Group',
                    gi_ref: 'GIBG0004',
                    investors_count: 3
                },
                rate_plan_id: 'rate-plan-adviser-mock-1',
                rate_plan: {
                    id: 'rate-plan-adviser-mock-1',
                    name: 'Standard adviser fees',
                    status: RatePlanStatusEnum.ACTIVE,
                    type: RatePlanTypeEnum.ADVISER,
                    adviser_id: MOCK_ADVISERS[0].id
                },
                subscription_type: SubscriptionTypeEnum.ADVISER,
                date_range: { start: '2024-03-01', end: null },
                investor_id: MOCK_INVESTORS[3].id,
                adviser_id: MOCK_ADVISERS[0].id,
                adviser: MOCK_ADVISERS[0],
                investors_count: 3
            }
        ];

        return rows.map(row => this._normaliseSubscriptionItem(row));
    }

    async _generateMocked(count = this.constructor.mockItemCount, overrides = {}) {
        const catalogue = this._buildMockCatalogue();
        let rows = catalogue.map(row => ({ ...row, ...overrides }));

        if (overrides.investor_id) {
            rows = rows.filter(row => row.investor_id === overrides.investor_id);
        }

        if (overrides.adviser_id) {
            rows = rows.filter(row => row.adviser_id === overrides.adviser_id);
        }

        if (overrides.rate_plan_id) {
            rows = rows.filter(row => row.rate_plan_id === overrides.rate_plan_id);
        }

        if (overrides.billing_group_id) {
            rows = rows.filter(row => row.billing_group_id === overrides.billing_group_id);
        }

        if (!rows.length) {
            rows = catalogue.slice(0, count).map(row => ({ ...row, ...overrides }));
        }

        this._saveMocked(catalogue);

        return rows.slice(0, count);
    }

    _filterMockedByDateRange(items, filters = {}) {
        let startFilter = null;
        let endFilter = null;

        if (Object.prototype.hasOwnProperty.call(filters, 'date_range.start')) {
            startFilter = filters['date_range.start'];
        } else if (Object.prototype.hasOwnProperty.call(filters, 'start_date')) {
            startFilter = filters.start_date;
        }

        if (Object.prototype.hasOwnProperty.call(filters, 'date_range.end')) {
            endFilter = filters['date_range.end'];
        } else if (Object.prototype.hasOwnProperty.call(filters, 'end_date')) {
            endFilter = filters.end_date;
        }

        if (!startFilter && !endFilter) {
            return items;
        }

        const parseFilterDate = value => {
            if (!value || typeof value !== 'string') {
                return null;
            }

            const parts = value.split(':');

            return parts.length > 1 ? parts.slice(1).join(':') : value;
        };

        const filterStart = parseFilterDate(startFilter);
        const filterEnd = parseFilterDate(endFilter);

        return items.filter(item => {
            const range = normaliseDateRange(item.date_range);

            if (!range?.start) {
                return false;
            }

            if (filterStart && range.start < filterStart) {
                return false;
            }

            if (filterEnd && range.start > filterEnd) {
                return false;
            }

            return true;
        });
    }

    _filterMocked(data, filters = {}) {
        let filtered = super._filterMocked(data, filters);

        return this._filterMockedByDateRange(filtered, filters);
    }

    async index_mock(offset = 0, limit = 100, sort_by = null, filters = {}) {
        void sort_by;

        let data = this._getMocked();

        if (!data.length) {
            data = this._buildMockCatalogue();
            this._saveMocked(data);
        }

        let filtered = this._filterMocked(data, filters);

        if (this.constructor.mockUseFilterOverrides && !filtered.length && filters && Object.keys(filters).length) {
            filtered = await this._generateMocked(this.constructor.mockItemCount, {});
            filtered = this._filterMocked(filtered, filters);
        }

        if (filters.billing_group_id) {
            const billingGroupId = String(filters.billing_group_id).replace(/^eq:/, '');

            filtered = filtered.filter(item => item.billing_group_id === billingGroupId);
        }

        filtered = [...filtered].sort((left, right) => {
            const leftStart = normaliseDateRange(left.date_range)?.start || '';
            const rightStart = normaliseDateRange(right.date_range)?.start || '';

            return rightStart.localeCompare(leftStart);
        });

        const total = filtered.length;

        if (offset !== null && limit !== null) {
            filtered = filtered.slice(offset, offset + limit);
        }

        const response = {
            status: 'success',
            data: filtered,
            metadata: {
                total,
                limit,
                offset
            }
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = this._filterIndexResponseData(response.data);

            this._insertModels(response.data);
        });
    }

    async get_mock(id) {
        await wait(100);

        const items = this._getMocked();

        if (!items.length) {
            this._saveMocked(this._buildMockCatalogue());
        }

        const item = this._getMocked().find(row => row.id === id);

        if (!item) {
            return {
                status: 'fail',
                code: 404,
                message: 'Not found'
            };
        }

        const response = {
            status: 'success',
            data: item
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    async create(data = {}, config = {}) {
        const response = await BaseApi.api.post(this.base_url, data, config);

        return this._respond(response, () => {
            if (response.data) {
                response.data = this._filterResponseData(response.data);
                this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
            }
        });
    }

    async create_mock(data = {}) {
        await wait(150);

        const billingGroupId = data.billing_group_id;
        const ratePlanId = data.rate_plan_id;
        const subscriptionType = data.rate_plan_type || data.subscription_type;
        const startDate = data.start_date;

        if (!billingGroupId || !ratePlanId || !subscriptionType || !startDate) {
            return {
                status: 'fail',
                code: 422,
                message: 'billing_group_id, rate_plan_id, rate_plan_type and start_date are required'
            };
        }

        let items = this._getMocked();

        if (!items.length) {
            items = this._buildMockCatalogue();
            this._saveMocked(items);
            items = this._getMocked();
        }

        const groupTypeItems = items.filter(
            item => item.billing_group_id === billingGroupId && item.subscription_type === subscriptionType
        );

        if (getUpcomingSubscription(groupTypeItems, { subscriptionType })) {
            return {
                status: 'fail',
                code: 409,
                message: 'A fee plan change is already scheduled for this type.'
            };
        }

        const currentEnd = clock(startDate).tz('Europe/London').subtract(1, 'day').format('YYYY-MM-DD');

        items = items.map(item => {
            if (item.billing_group_id !== billingGroupId || item.subscription_type !== subscriptionType) {
                return item;
            }

            const range = normaliseDateRange(item.date_range);

            if (!range?.start || range.start >= startDate) {
                return item;
            }

            if (range.end && range.end < startDate) {
                return item;
            }

            return {
                ...item,
                date_range: {
                    start: range.start,
                    end: currentEnd
                }
            };
        });

        const sibling = items.find(item => item.billing_group_id === billingGroupId) || {};
        const existingPlan = items.find(item => item.rate_plan_id === ratePlanId || item.rate_plan?.id === ratePlanId);
        const storedPlan = RatePlan.find(ratePlanId);

        const { faker } = await import('@faker-js/faker');

        const created = this._normaliseSubscriptionItem({
            id: faker.string.uuid(),
            gi_ref: `GIBS${String(items.length + 1).padStart(4, '0')}`,
            billing_group_id: billingGroupId,
            billing_group: sibling.billing_group || { id: billingGroupId, name: billingGroupId },
            rate_plan_id: ratePlanId,
            rate_plan: existingPlan?.rate_plan || {
                id: ratePlanId,
                name: storedPlan?.name || ratePlanId,
                status: RatePlanStatusEnum.ACTIVE,
                type: subscriptionType,
                adviser_id: sibling.adviser_id || storedPlan?.adviser_id || null
            },
            subscription_type: subscriptionType,
            date_range: { start: startDate, end: null },
            investor_id: sibling.investor_id || null,
            adviser_id: sibling.adviser_id || storedPlan?.adviser_id || null,
            adviser: sibling.adviser || null,
            investors_count: sibling.investors_count ?? sibling.billing_group?.investors_count ?? null
        });

        items = [...items, created];
        this._saveMocked(items);

        const response = {
            status: 'success',
            data: created
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    async cancel(id, config = { base_url: null }) {
        return this.remove(id, config);
    }

    async cancel_mock(id) {
        await wait(150);

        let items = this._getMocked();

        if (!items.length) {
            items = this._buildMockCatalogue();
            this._saveMocked(items);
        }

        items = this._getMocked();
        const target = items.find(item => item.id === id);

        if (!target) {
            return {
                status: 'fail',
                code: 404,
                message: 'Not found'
            };
        }

        if (!isSubscriptionCancellable(target)) {
            return {
                status: 'fail',
                code: 422,
                message: 'This subscription has already started or cannot be cancelled.'
            };
        }

        const targetRange = normaliseDateRange(target.date_range);
        const predecessorEnd = targetRange?.start
            ? clock(targetRange.start).tz('Europe/London').subtract(1, 'day').format('YYYY-MM-DD')
            : null;

        const predecessor = items.find(item => {
            if (item.id === target.id) {
                return false;
            }

            if (item.billing_group_id !== target.billing_group_id) {
                return false;
            }

            if (item.subscription_type !== target.subscription_type) {
                return false;
            }

            const range = normaliseDateRange(item.date_range);

            return range?.end === predecessorEnd;
        });

        if (!predecessor) {
            return {
                status: 'fail',
                code: 422,
                message: 'This subscription cannot be cancelled because no predecessor was found.'
            };
        }

        const nextItems = items
            .filter(item => item.id !== id)
            .map(item => {
                if (item.id !== predecessor.id) {
                    return item;
                }

                const predecessorRange = normaliseDateRange(item.date_range) || { start: null, end: null };

                return {
                    ...item,
                    date_range: {
                        ...predecessorRange,
                        end: null
                    }
                };
            });

        this._saveMocked(nextItems);

        const updatedPredecessor = nextItems.find(item => item.id === predecessor.id);

        const response = {
            status: 'success'
        };

        return this._respond(response, () => {
            this._removeItemInCollections(id);

            if (updatedPredecessor) {
                this._updateItemInCollections(updatedPredecessor.id, updatedPredecessor);
                this._insertModel(updatedPredecessor, this.ApiResponseFormatEnum.DETAIL);
            }
        });
    }
}

export default SubscriptionApi;
